import { EventContext, EventType } from "~/types/Analytics";
import { BusinessEvent, BusinessEventHandler, Click, CookieConsentUpdate, ExperienceCardClicked, ExperienceCardViewed, PageView, PaymentFormType } from "../../businessLayer";
import { WlWindow } from "typings/globals/cordova";
import { TrekkerEventHandler } from "react-trekker";
import Cookies from 'js-cookie';
import { AB_TEST_COOKIE_NAME } from "~/constants";

function getEcommerceData(context: BusinessEvent | EventContext) {
  if ("experience" in context && context.experience) {
    return {
      value: context.experience?.pricing?.private_price?.[context?.nrOfAdults] *
        context?.nrOfAdults,
      currency: "EUR",
      items: [{
        item_id: context.experience?.id,
        item_name: context.experience?.title,
        currency: "EUR",
        price: context.nrOfAdults
          ? context.experience?.pricing?.private_price?.[context?.nrOfAdults]
          : context.experience?.pricing?.advertised_price_origin?.amount.value,
        quantity: context.nrOfAdults,
        location_id: context.experience?.itinerary?.destination,
        item_brand: context.experience?.proposition?.distributionChannel,
        item_category: context.experience?.itinerary?.tier,
        item_category2: context.experience?.itinerary?.concept,
        index: context["position"],
      }],
    };
  }
  if ("trip" in context && context.trip) {
    return {
      value: context.trip.total_price,
      currency: "EUR",
      transaction_id: context?.trip?.id,
      shipping: context.trip?.price?.service_fee_amount.value,
      items: [{
        item_id: context.trip?.experience_id,
        item_name: context.trip.title,
        quantity: context.trip.number_of_guest,
        price: context.trip.price_pp,
      }],
    };
  }
  return {};
}

declare const window: WlWindow;

const ga4State = {
  consentInited: false,
};
// Handles cookie consent inital state
export const ga4CookieConsentEventListener: TrekkerEventHandler<EventContext, EventType> = (evt) => {
  // initially set consent correct
  if (evt.context.cookieConsent.analytics && !ga4State.consentInited) {
    window.ga4Tag?.("consent", "update", {
      ad_storage: evt.context.cookieConsent.advertisement
        ? "granted"
        : "denied",
      analytics_storage: evt.context.cookieConsent.analytics
        ? "granted"
        : "denied",
    });
    ga4State.consentInited = true;
  }
};

function addDefaultParameters(
  inputParams: object,
  evt: BusinessEvent
) {

  const experimentEventData = {};
  evt.experiments?.forEach((exp) => {
    experimentEventData[`experiment_${exp.id}`] = exp.assignedVariant;
  });

  // Track special store front ab test
  experimentEventData['storefront_ab_test'] = Cookies.get(AB_TEST_COOKIE_NAME) || 'not_in_test';

  return {
    traffic_type: evt.traffic_type,
    account_is_host: evt.account_is_host,
    account_id: evt.account_id,
    application: 'ue',
    ...experimentEventData,
    ...inputParams,
  };
}

// SEE https://developers.google.com/analytics/devguides/collection/gtagjs/enhanced-ecommerce#measure_checkouts
// for event details
export const ga4EcommerceListener: BusinessEventHandler = (evt: BusinessEvent) => {
  console.log("[GA4] received event", evt.type, evt, evt.constructor.name);
  if (evt instanceof CookieConsentUpdate) {
    window.ga4Tag?.("consent", "update", {
      ad_storage: evt.advertisement ? "granted" : "denied",
      analytics_storage: evt.analytics ? "granted" : "denied",
    });
  }

  if (evt.type === 'PageView') {
    const pageView = evt as PageView;
    const eventData = addDefaultParameters({
      page_title: pageView.title,
      page_location: pageView.location,
      page_path: pageView.path,
    }, pageView);

    window.ga4Tag?.("event", "page_view", eventData);
  }

  if (evt.type === 'ExperienceCardViewed') {
    const experienceCardViewed = evt as ExperienceCardViewed;
    const { items } = getEcommerceData(evt);
    window.ga4Tag?.("event", "view_item", addDefaultParameters({
      item_list_name: window.document.title,
      item_list_id: experienceCardViewed.analyticsListName || window.location.pathname,
      items,
    }, experienceCardViewed));
  }

  if (evt.type === 'ExperienceCardClicked') {
    const experienceCardClicked = evt as ExperienceCardClicked;
    const { items } = getEcommerceData(evt);
    window.ga4Tag?.("event", "select_content", addDefaultParameters({
      item_list_name: window.document.title,
      item_list_id: experienceCardClicked.analyticsListName || window.location.pathname,
      items,
    }, experienceCardClicked));
  }
  if (
    evt.type === 'HostSelected' ||
    evt.type === 'PaymentOptionSelected' ||
    evt.type === 'DateSelected' ||
    evt.type === 'NumberOfGuestsSelected'
  ) {
    window.ga4Tag?.("event", "set_checkout_option", addDefaultParameters({
      option: evt.type,
      ...getEcommerceData(evt),
    }, evt));
  }

  if (evt.type === 'BookButtonClicked') {
    window.ga4Tag?.("event", "begin_checkout", addDefaultParameters({
      option: evt.type,
      ...getEcommerceData(evt),
    }, evt));
  }

  if (evt.type === 'CheckoutPageViewed') {
    window.ga4Tag?.("event", "checkout_progress", addDefaultParameters({
      option: evt.type,
      ...getEcommerceData(evt),
    }, evt));
  }

  if (evt.type === 'PaymentFormType') {
    const paymentFormType = evt as PaymentFormType;
    window.ga4Tag?.("event", "payment_form_type", addDefaultParameters({
      option: paymentFormType.variant,
    }, paymentFormType));
  }

  if (evt.type === 'PaymentOptionSelected') {
    window.ga4Tag?.("event", "set_checkout_option", addDefaultParameters({
      option: "p-selected",
      ...getEcommerceData(evt),
    }, evt));
  }
  if (evt.type === 'BookingConfirmed') {
    window.ga4Tag?.("event", "purchase", addDefaultParameters({
      option: "payment-confirmed",
      ...getEcommerceData(evt),
    }, evt));
  }
  if (evt.type === 'Click') {
    const click = evt as Click;
    window.ga4Tag?.("event", "click", addDefaultParameters({
      option: click.target
    }, click));
  }
};
